/**
 * Plugin configuration store using Zustand.
 */

import { create } from 'zustand';
import apiClient from '@/api/client';

import type {
  PluginConfig,
  PluginConfigCreate,
  PluginTestResult,
} from '@/types';

interface PluginStoreState {
  configs: PluginConfig[];
  loading: boolean;
  testResults: Record<number, PluginTestResult>;
  error: string | null;

  // Actions
  fetchConfigs: () => Promise<void>;
  saveConfig: (id: number | null, config: PluginConfigCreate, scriptFile?: File) => Promise<PluginConfig>;
  runTest: (configId: number) => Promise<PluginTestResult>;
  deleteConfig: (configId: number) => Promise<void>;
  clearError: () => void;
}

const API_BASE = 'plugins';

export const usePluginStore = create<PluginStoreState>()((set, get) => ({
  configs: [],
  loading: false,
  testResults: {},
  error: null,

  fetchConfigs: async () => {
    set({ loading: true, error: null });
    try {
      const response = await apiClient.get(`${API_BASE}/`);
      set({ configs: response.data, loading: false });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        '获取插件配置失败';
      set({ error: message, loading: false });
      throw error;
    }
  },

  saveConfig: async (id: number | null, config: PluginConfigCreate, scriptFile?: File) => {
    set({ loading: true, error: null });
    try {
      let response;

      if (scriptFile) {
        // Use FormData only when there's a file to upload
        const formData = new FormData();
        formData.append('project', config.project.toString());
        formData.append('plugin_type', config.plugin_type);
        formData.append('git_url', config.git_url || '');
        formData.append('base_branch', config.base_branch || '');
        formData.append('is_active', config.is_active ? 'true' : 'false');
        formData.append('input_kinds', config.input_kinds || '');
        formData.append('auto_merge_mcu_pr', config.auto_merge_mcu_pr ? 'true' : 'false');
        formData.append('script_file', scriptFile);

        if (id) {
          response = await apiClient.put(`${API_BASE}/${id}/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          response = await apiClient.post(`${API_BASE}/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
      } else {
        // Use JSON when no file upload
        const jsonData = {
          project: config.project,
          plugin_type: config.plugin_type,
          git_url: config.git_url,
          base_branch: config.base_branch,
          is_active: config.is_active,
          input_kinds: config.input_kinds || '',
          auto_merge_mcu_pr: config.auto_merge_mcu_pr ?? false,
        };

        if (id) {
          response = await apiClient.put(`${API_BASE}/${id}/`, jsonData, {
            headers: { 'Content-Type': 'application/json' },
          });
        } else {
          response = await apiClient.post(`${API_BASE}/`, jsonData, {
            headers: { 'Content-Type': 'application/json' },
          });
        }
      }

      // Update configs list
      const configs = get().configs;
      if (id) {
        set({
          configs: configs.map((c) => (c.id === id ? response.data : c)),
          loading: false,
        });
      } else {
        set({
          configs: [...configs, response.data],
          loading: false,
        });
      }

      return response.data;
    } catch (error: unknown) {
      const data = (error as any)?.response?.data;
      let message = '保存插件配置失败';

      if (data?.error) {
        message = data.error;
        if (data?.detail) {
          const detail = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
          message = `${message}：${detail}`;
        }
      } else if (data?.detail) {
        message = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
      }

      set({ error: message, loading: false });
      throw error;
    }
  },

  runTest: async (configId: number) => {
    set({ loading: true, error: null });
    try {
      const response = await apiClient.post(`${API_BASE}/${configId}/test/`);
      set({
        testResults: {
          ...get().testResults,
          [configId]: response.data,
        },
        loading: false,
      });
      return response.data;
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Quick Test 失败';
      set({ error: message, loading: false });
      throw error;
    }
  },

  deleteConfig: async (configId: number) => {
    set({ loading: true, error: null });
    try {
      await apiClient.delete(`${API_BASE}/${configId}/`);
      set({
        configs: get().configs.filter((c) => c.id !== configId),
        loading: false,
      });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        '删除插件配置失败';
      set({ error: message, loading: false });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
